import {
  MapPin,
  Mail,
  Phone,
  MessageCircle,
  Instagram,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT, waLink } from "@/data/site";
import { Reveal, SectionKicker } from "./Reveal";

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="py-24 sm:py-32 bg-[#050505]"
    >
      <div className="container-x grid lg:grid-cols-2 gap-12 lg:gap-16">
        <Reveal>
          <SectionKicker>{c.kicker}</SectionKicker>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {c.title}
          </h2>
          <p className="mt-4 text-base text-zinc-400 leading-relaxed max-w-md">
            {c.subtitle}
          </p>

          <div className="mt-10 space-y-6">
            <InfoRow icon={MapPin} label={c.location}>
              <div className="text-sm text-zinc-400 mt-1">
                {c.areaLabel}: {c.areas}
              </div>
            </InfoRow>
            <InfoRow icon={Mail} label={c.emailLabel}>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email"
                className="text-zinc-300 hover:text-brand transition-colors break-all"
              >
                {CONTACT.email}
              </a>
            </InfoRow>
            <InfoRow icon={Phone} label={c.phoneLabel}>
              <div className="flex flex-col gap-1">
                <a href={`tel:+${CONTACT.phone1Raw}`} className="text-zinc-300 hover:text-brand transition-colors">
                  {CONTACT.phone1}
                </a>
                <a href={`tel:+${CONTACT.phone2Raw}`} className="text-zinc-300 hover:text-brand transition-colors">
                  {CONTACT.phone2}
                </a>
              </div>
            </InfoRow>
            <InfoRow icon={Instagram} label="Instagram">
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-300 hover:text-brand transition-colors"
              >
                {CONTACT.instagramHandle}
              </a>
            </InfoRow>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#111] to-[#0a0a0a] p-8 sm:p-10">
            <h3 className="font-heading text-2xl font-bold text-white">
              {c.quote}
            </h3>
            <p className="mt-3 text-sm text-zinc-400">{c.subtitle}</p>
            <div className="mt-8 space-y-3">
              <a
                href={waLink(c.whatsappMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex items-center justify-center gap-2 h-14 rounded-full bg-[#25D366] hover:bg-[#1eb857] text-white font-semibold transition-colors"
              >
                <MessageCircle className="h-5 w-5" />
                {c.whatsapp}
              </a>
              <a
                href={`tel:+${CONTACT.phone1Raw}`}
                data-testid="contact-call-btn"
                className="flex items-center justify-center gap-2 h-14 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold transition-colors"
              >
                <Phone className="h-5 w-5" />
                {c.call} · {CONTACT.phone1}
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-btn"
                className="flex items-center justify-center gap-2 h-14 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold transition-colors"
              >
                <Mail className="h-5 w-5" />
                {c.emailLabel}
              </a>
            </div>
            <div className="mt-8 rounded-2xl bg-brand/10 border border-brand/20 p-4 text-center">
              <p className="text-sm text-brand-light font-medium">
                {t.hero.badge}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-4">
    <div className="h-11 w-11 shrink-0 rounded-xl bg-brand/10 grid place-items-center">
      <Icon className="h-5 w-5 text-brand" />
    </div>
    <div>
      <div className="font-heading font-semibold text-white">{label}</div>
      {children}
    </div>
  </div>
);
